package com.islamicmedia.shared

enum class Environment {
    DEV,
    PROD
}

object AppConfig {
    /**
     * Current environment, resolved at BUILD TIME from [BuildEnv] (the `app_env` Gradle property /
     * APP_ENV env var — see shared/build.gradle.kts + Dockerfile / fly*.toml [build.args]).
     * No longer a hand-edited source line: the prod Docker image is built with APP_ENV=prod, so
     * `fly deploy --config fly.toml` always ships a PROD frontend; local builds default to DEV.
     * DEV  → dev Supabase project + dev Fly.io server
     * PROD → production Supabase project + production Fly.io server
     */
    val ENVIRONMENT = if (BuildEnv.NAME == "PROD") Environment.PROD else Environment.DEV

    /**
     * Platform-set guard: force PROD regardless of the compile-time [ENVIRONMENT]. The Android
     * Application and iOS entry point set this `true` on RELEASE (non-debuggable) builds at startup,
     * so a release built WITHOUT `-Papp_env=prod` — e.g. straight from Android Studio or an Xcode
     * archive — can never point at the DEV backend or show the dev switcher. Debug builds leave it
     * false → DEV + switcher for testing. Must be set before any URL/key getter is first read.
     */
    var forceProd: Boolean = false

    /** True only on a DEV build that hasn't been force-promoted to PROD (drives dev-only affordances). */
    val isDev: Boolean get() = !forceProd && ENVIRONMENT == Environment.DEV

    /**
     * Runtime override for the active backend. Only honored on dev builds (see [isDev]) so
     * production users can never accidentally point at the dev backend.
     *
     * Set this once at app startup (web reads from `localStorage["env-override"]`). After that
     * [activeEnvironment] resolves to this value for all URL/key getters below. Toggling at runtime
     * requires a page reload because the HTTP client and ViewModel cache the URL at construction.
     */
    var runtimeOverride: Environment? = null

    /** The environment actually in effect — runtime override on dev builds, else PROD. */
    val activeEnvironment: Environment
        get() = if (isDev) (runtimeOverride ?: Environment.DEV) else Environment.PROD

    /**
     * Master switch for all payment/subscription features.
     * When false, the paywall is bypassed and all users get full access.
     * Auth/login flow remains active regardless of this flag.
     */
    // Enabled to test the subscription billing flow. Keep true for the eventual prod launch,
    // but it requires real Stripe configured on prod before shipping. See PRD Phase 7.
    const val PAYMENTS_ENABLED = true

    /**
     * Whether anyone gets to choose between a Quick check and a Full analysis.
     *
     * OFF since 2026-09-23: every new search is FULL for every user, and no quick/full picker is
     * shown anywhere (the search-screen toggle, the "Quick Analyze" button in the title preview).
     * The quick-check code is deliberately left in place, so setting this back to true restores
     * the subscriber-only choice exactly as it was. Old quick results in history keep their
     * "Quick check" pill and can still be upgraded to a full analysis.
     */
    const val SEARCH_MODE_CHOICE_ENABLED = false

    // TEMP (UI-only preview): show the new subscribe paywall from Fitrah_Filter_User_Flow.pdf —
    // what unlocking full analysis gives + Annual/Monthly pricing — instead of the old plan-list
    // paywall. Flip to false for the old design. Purely visual: no gating or flow changes behind it.
    const val PAYWALL_NEW_STYLE = true

    // Hide the paywall's second page (the plan chooser) and send the intro CTA straight to Stripe
    // with [PAYWALL_DIRECT_PLAN_KEY]. The plan chooser is only hidden, not removed — flip this to
    // false to bring it back with no other change.
    const val PAYWALL_SKIP_PLAN_STEP = true

    // Which plan the intro CTA buys when the plan chooser is hidden.
    //
    // ⚠️ The keys read backwards. Live /api/pricing serves:
    //      "pro_29" → "ONLY $9 USD"  ($9 today, $29 from next month — the intro-coupon plan)
    //      "annual" → "$ 149 USD"    (billed annually)
    // so "pro_29" is the $9 offer and "annual" is the yearly one, NOT the other way round. The
    // fallback PricingConfig.DEFAULT in this repo has them the wrong way round and is stale; the
    // server is authoritative. Verify against /api/pricing before ever changing this — it decides
    // what a user is charged.
    const val PAYWALL_DIRECT_PLAN_KEY = "pro_29"

    // ── Supabase ────────────────────────────────────────────
    val SUPABASE_URL: String get() = when (activeEnvironment) {
        Environment.DEV  -> "https://vpimzjsczhohcsboxndo.supabase.co"
        Environment.PROD -> "https://ydcpxxuxsucwchhygqrn.supabase.co"
    }

    val SUPABASE_ANON_KEY: String get() = when (activeEnvironment) {
        Environment.DEV  -> "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZwaW16anNjemhvaGNzYm94bmRvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUxOTI3NDMsImV4cCI6MjA5MDc2ODc0M30.7s0cLOZIrzrfj7c8qP3S01pFTWFGR7clwjWXAxWtSPU"
        Environment.PROD -> "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlkY3B4eHV4c3Vjd2NoaHlncXJuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA0NDExNDcsImV4cCI6MjA4NjAxNzE0N30.RV_eL9ikPdP5U4miG8e4JLIOxsUIr5jXks_jI_e_8Ck"
    }

    // ── Server ──────────────────────────────────────────────
    val SERVER_BASE_URL: String get() = when (activeEnvironment) {
        Environment.DEV  -> "https://fitrah-filter-dev.fly.dev"  // TODO: replace after fly launch
        Environment.PROD -> "https://islamic-media-api.fly.dev"
    }

    // ── Web (user-facing site: auth-callback + billing return pages) ──
    // Prod uses the custom domain; the same Fly app also answers on SERVER_BASE_URL.
    val WEB_BASE_URL: String get() = when (activeEnvironment) {
        Environment.DEV  -> "https://fitrah-filter-dev.fly.dev"
        Environment.PROD -> "https://fitrahfilter.com"
    }

    // ── Native Google Sign-In (mobile) ──────────────────────
    /**
     * OAuth client IDs for the NATIVE Google sign-in flow — Android's Credential Manager sheet and
     * iOS's in-app GoogleSignIn sheet. They live in the Google Cloud project behind Firebase, and
     * each one must also be listed under Supabase → Auth → Providers → Google → "Authorized Client
     * IDs" (on BOTH the dev and prod projects) or the ID token exchange is rejected.
     *
     * Leave either BLANK and that platform silently falls back to the old browser OAuth round-trip,
     * so an unconfigured build still signs in — it just opens a browser like before.
     */
    /** Web client ID — Android passes it as `serverClientId`; it is the audience Supabase expects. */
    const val GOOGLE_WEB_CLIENT_ID = "806407318633-hsuir94l40h8g0l44gdntf66q6v9a25j.apps.googleusercontent.com"

    /** iOS client ID (bundle com.lgrs.fitrahfilter). Its REVERSED form must also be a URL scheme
     *  in iosApp/iosApp/Info.plist — see the CFBundleURLTypes entry there. */
    const val GOOGLE_IOS_CLIENT_ID = "806407318633-hqqbdnse7i4mbkh73t39c0f5tp7fc9h7.apps.googleusercontent.com"
}
